import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function AdminAbandoned() {
  const [carts, setCarts] = useState([]);
  const [selected, setSelected] = useState(new Set());

  const reload = useCallback(
    () => adminApi().get("/admin/abandoned-carts").then((r) => { setCarts(r.data); setSelected(new Set()); }).catch(() => {}),
    [],
  );
  useEffect(() => { reload(); }, [reload]);

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this abandoned cart?")) return;
    try { await adminApi().delete(`/admin/abandoned-carts/${id}`); toast.success("Removed"); reload(); }
    catch { toast.error("Delete failed"); }
  };

  const deleteBulk = async () => {
    const ids = Array.from(selected);
    if (!window.confirm(`Delete ${ids.length} abandoned cart${ids.length === 1 ? "" : "s"}?`)) return;
    try {
      const r = await adminApi().post("/admin/abandoned-carts/delete-bulk", { ids });
      toast.success(`Deleted ${r.data.deleted}`);
      reload();
    } catch { toast.error("Bulk delete failed"); }
  };

  const cartMsg = (a, items) => {
    const site = window.location.origin;
    return `Hi ${a.customer?.name || "there"}! 👋 Noticed you left a few hampers in your cart: ${items}. Use code COMEBACK10 for 10% off — complete your order now: ${site}/cart`;
  };

  return (
    <div className="mt-6 space-y-3" data-testid="admin-abandoned-tab">
      {carts.length === 0 ? (
        <p className="text-[#5C5555]">No abandoned carts yet.</p>
      ) : (
        <div className="flex items-center justify-between flex-wrap gap-2 bg-[#F2E8E4]/50 border border-[#E5DBD8] px-3 py-2 rounded">
          <label className="inline-flex items-center gap-2 text-sm font-hand cursor-pointer">
            <input
              type="checkbox"
              checked={selected.size > 0 && selected.size === carts.length}
              onChange={(e) => setSelected(e.target.checked ? new Set(carts.map((a) => a.id)) : new Set())}
              data-testid="abandoned-select-all"
              className="w-4 h-4 accent-[#7A1F2B] cursor-pointer"
            />
            Select all
          </label>
          <div className="text-xs text-[#5C5555]">{selected.size} selected</div>
          <div className="flex gap-2">
            {selected.size > 0 && (
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                data-testid="abandoned-clear-selection"
                className="text-xs px-3 py-1.5 border border-[#E5DBD8] hover:bg-white rounded"
              >Clear</button>
            )}
            <button
              type="button"
              disabled={selected.size === 0}
              onClick={deleteBulk}
              data-testid="abandoned-bulk-delete"
              className="text-xs px-3 py-1.5 bg-[#7A1F2B] text-white rounded hover:bg-[#5C1520] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              <Trash2 className="w-3.5 h-3.5"/> Delete selected
            </button>
          </div>
        </div>
      )}
      {carts.map((a) => {
        const phone = (a.customer?.phone || "").replace(/[^0-9]/g, "");
        const items = (a.items || []).map((it) => `${it.name}×${it.quantity}`).join(", ");
        const wa = phone ? `https://wa.me/91${phone.slice(-10)}?text=${encodeURIComponent(cartMsg(a, items))}` : null;
        const when = a.updated_at || a.created_at;
        const checked = selected.has(a.id);
        return (
          <div key={a.id} className={`bg-white border ${checked ? "border-[#7A1F2B]" : "border-[#E5DBD8]"} p-4 text-sm flex gap-3 flex-wrap`}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setSelected((prev) => { const n = new Set(prev); if (e.target.checked) n.add(a.id); else n.delete(a.id); return n; })}
              data-testid={`abandoned-checkbox-${a.id}`}
              className="mt-1 w-4 h-4 accent-[#7A1F2B] cursor-pointer shrink-0"
            />
            <div className="flex-1 min-w-[200px]">
              <p className="font-serif text-lg">
                {a.customer?.name || "Anonymous"} <span className="text-[#7A1F2B]">₹{(a.total || 0).toLocaleString("en-IN")}</span>
                {a.converted && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">CONVERTED</span>}
              </p>
              <p className="text-xs text-[#5C5555]">{a.customer?.email || "—"} · {a.customer?.phone || "—"} · {when ? new Date(when).toLocaleString() : "—"}</p>
              <p className="text-[#5C5555] mt-1">{items}</p>
            </div>
            <div className="flex items-center gap-2 self-start">
              {wa ? (
                <a href={wa} target="_blank" rel="noreferrer" data-testid={`wa-recover-${a.id}`} className="btn-whatsapp">Send WhatsApp</a>
              ) : (
                <span className="text-xs text-[#5C5555]">No phone</span>
              )}
              <button
                type="button"
                onClick={() => deleteOne(a.id)}
                data-testid={`abandoned-delete-${a.id}`}
                className="p-2 border border-[#E5DBD8] hover:bg-[#FFE7F1] rounded"
                aria-label="delete"
              >
                <Trash2 className="w-4 h-4 text-[#7A1F2B]"/>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
